import {useEffect, useRef, useState} from "react";
import {Product, ProductsData} from "../types/Page";
import api from "../../api";
import Header from "./Components/Header";
import PageHero from "./Components/PageHero";
import '../../styles/Products.scss';
import {IMAGE_URL} from "../../path";
import FooterWithoutSignup from "./Components/FooterWithoutSignup";
import {useNavigate} from "react-router-dom";




interface MobileProductProps {
    product: Product;
    selectedCaps: Record<number, number>;
    setSelectedCaps: React.Dispatch<
        React.SetStateAction<Record<number, number>>
    >;
    expandedProduct: Product | null;
    toggleExpandedProduct: (product: Product) => void;
}


function MobileProduct({
                           product,
                           selectedCaps,
                           setSelectedCaps,
                           expandedProduct,
                           toggleExpandedProduct,
                       }: MobileProductProps) {

    const selectedCap =
        selectedCaps[product.id] ?? 0;

    const selectedSize =
        product.sizes[selectedCap] ??
        product.sizes[0];

    const isExpanded =
        expandedProduct?.id === product.id;

    const navigate = useNavigate();

    return (
        <div
            className={`mobile-product ${
                isExpanded ? "expanded" : ""
            }`}
            onClick={() => navigate(`/product/${product.slug}`)}
            style={{
                "--product-background-color":
                product.color_code,
            } as React.CSSProperties}
        >

            <img
                src={`${IMAGE_URL}/${selectedSize.pack_shot}`}
                alt={
                    product.name +
                    selectedSize.capacity
                }
            />

            <div className="mobile-product-info">

                <h2>
                    {product.name}
                </h2>

                <h3>
                    {selectedSize.title} (
                    {selectedSize.capacity}
                    )
                </h3>

                {isExpanded && (
                    <div>

                        <p>
                            {product.description}
                        </p>

                        <div className="capacities">

                            {product.sizes.map(
                                (size, index) => (
                                    <div
                                        className={`capacities-item ${
                                            selectedCap ===
                                            index
                                                ? "active"
                                                : ""
                                        }`}
                                        key={size.id}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setSelectedCaps((prev) => ({
                                                ...prev,
                                                [product.id]: index,
                                            }));
                                        }}
                                    >
                                        {size.capacity}
                                    </div>
                                )
                            )}

                        </div>

                    </div>
                )}

                <div
                    className="bottom-icon"
                    onClick={(e) => {
                        e.stopPropagation();

                        toggleExpandedProduct(product);
                    }}
                >
                    {isExpanded ? "-" : "+"}
                </div>

            </div>

        </div>
    );
}


interface MobileProductRangeProps {
    products: Product[];
    selectedCaps: Record<number, number>;
    setSelectedCaps: React.Dispatch<
        React.SetStateAction<Record<number, number>>
    >;
    expandedProduct: Product | null;
    toggleExpandedProduct: (product: Product) => void;
}


function MobileProductRange({
                                products,
                                selectedCaps,
                                setSelectedCaps,
                                expandedProduct,
                                toggleExpandedProduct,
                            }: MobileProductRangeProps) {

    /*
    |--------------------------------------------------------------------------
    | CAROUSEL STATE
    |--------------------------------------------------------------------------
    */

    const [currentIndex, setCurrentIndex] =
        useState(products.length);

    const [disableTransition, setDisableTransition] =
        useState(false);

    const productsInnerRef =
        useRef<HTMLDivElement>(null);

    const viewportRef =
        useRef<HTMLDivElement>(null);



    const [productWidth, setProductWidth] =
        useState(0);

    const [viewportWidth, setViewportWidth] =
        useState(0);


    /*
    |--------------------------------------------------------------------------
    | PRODUCT WIDTH
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Use the actual rendered mobile-product width.
    |
    | This means one click moves exactly one card,
    | regardless of viewport size or CSS rounding.
    |
    */

    useEffect(() => {

        function updateProductWidth() {

            if (!viewportRef.current) {
                return;
            }

            const viewport =
                viewportRef.current;

            const width =
                viewport.getBoundingClientRect().width;

            const product =
                viewport.querySelector<HTMLElement>(
                    ".mobile-product"
                );

            const actualProductWidth =
                product?.getBoundingClientRect().width ??
                width / 2;

            setViewportWidth(width);

            setProductWidth(
                actualProductWidth
            );
        }

        updateProductWidth();

        window.addEventListener(
            "resize",
            updateProductWidth
        );

        return () => {
            window.removeEventListener(
                "resize",
                updateProductWidth
            );
        };

    }, []);


    /*
    |--------------------------------------------------------------------------
    | RESET INDEX WHEN PRODUCTS CHANGE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setCurrentIndex(
            products.length
        );

    }, [products.length]);


    /*
    |--------------------------------------------------------------------------
    | CAROUSEL NAVIGATION
    |--------------------------------------------------------------------------
    */

    function scrollProducts(
        direction: "left" | "right"
    ) {

        if (!products.length) {
            return;
        }

        setCurrentIndex((current) => {

            if (direction === "right") {
                return current + 1;
            }

            return current - 1;
        });
    }


    /*
    |--------------------------------------------------------------------------
    | INFINITE LOOP
    |--------------------------------------------------------------------------
    */

    function handleTransitionEnd() {

        if (!products.length) {
            return;
        }

        const productCount =
            products.length;




        /*
        |--------------------------------------------------------------------------
        | RIGHT
        |--------------------------------------------------------------------------
        */

        if (
            currentIndex >=
            productCount * 2
        ) {

            setDisableTransition(true);

            setCurrentIndex(
                productCount
            );

            requestAnimationFrame(() => {

                requestAnimationFrame(() => {
                    setDisableTransition(false);
                });

            });

            return;
        }


        /*
        |--------------------------------------------------------------------------
        | LEFT
        |--------------------------------------------------------------------------
        */

        if (
            currentIndex <
            productCount
        ) {

            setDisableTransition(true);

            setCurrentIndex(
                productCount * 2 - 1
            );

            requestAnimationFrame(() => {

                requestAnimationFrame(() => {
                    setDisableTransition(false);
                });

            });
        }
    }


    /*
    |--------------------------------------------------------------------------
    | THREE COPIES
    |--------------------------------------------------------------------------
    */

    const carouselProducts = [
        ...products,
        ...products,
        ...products,
    ];


    /*
    |--------------------------------------------------------------------------
    | CENTRE ACTIVE PRODUCT
    |--------------------------------------------------------------------------
    */

    const mobileOffset =
        (viewportWidth -
            productWidth) /
        2;

    const translateX =
        -(currentIndex * productWidth) +
        mobileOffset;


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <section className="mobile-products-section">

            <div className="mobile-products">

                {/* LEFT ARROW */}

                <button
                    type="button"
                    className="mobile-products-arrow mobile-products-arrow-left"
                    onClick={() =>
                        scrollProducts("left")
                    }
                    aria-label="Previous product"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="38"
                        height="38"
                        viewBox="0 0 38 38"
                    >
                        <circle
                            cx="19"
                            cy="19"
                            r="18"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                        />

                        <path
                            d="M21.5 11.5L14 19L21.5 26.5"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>


                {/* VIEWPORT */}

                <div
                    className="mobile-products-viewport"
                    ref={viewportRef}
                >

                    {/* TRACK */}

                    <div
                        className="mobile-products-inner"
                        ref={productsInnerRef}
                        onTransitionEnd={
                            handleTransitionEnd
                        }
                        style={{
                            transform:
                                `translateX(${translateX}px)`,

                            transition:
                                disableTransition
                                    ? "none"
                                    : "transform 0.5s ease",
                        }}
                    >

                        {carouselProducts.map(
                            (product, index) => (

                                <MobileProduct
                                    key={`${product.id}-${index}`}
                                    product={product}
                                    selectedCaps={
                                        selectedCaps
                                    }
                                    setSelectedCaps={
                                        setSelectedCaps
                                    }
                                    expandedProduct={
                                        expandedProduct
                                    }
                                    toggleExpandedProduct={
                                        toggleExpandedProduct
                                    }
                                />

                            )
                        )}

                    </div>

                </div>


                {/* RIGHT ARROW */}

                <button
                    type="button"
                    className="mobile-products-arrow mobile-products-arrow-right"
                    onClick={() =>
                        scrollProducts("right")
                    }
                    aria-label="Next product"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="38"
                        height="38"
                        viewBox="0 0 38 38"
                    >
                        <circle
                            cx="19"
                            cy="19"
                            r="18"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                        />

                        <path
                            d="M16.5 11.5L24 19L16.5 26.5"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

            </div>

        </section>
    );
}


/*
|--------------------------------------------------------------------------
| PRODUCTS PAGE
|--------------------------------------------------------------------------
*/

function Products() {

    const navigate = useNavigate();
    const [productsData, setProductsData] =
        useState<ProductsData | null>(null);

    const [selectedCaps, setSelectedCaps] =
        useState<Record<number, number>>({});

    const [expandedProduct, setExpandedProduct] =
        useState<Product | null>(null);


    /*
    |--------------------------------------------------------------------------
    | FETCH PRODUCTS
    |--------------------------------------------------------------------------
    */

    async function fetchProductData() {

        try {

            const res =
                await api.get('/v1/products');

            console.log(res);

            setProductsData(
                res.data
            );

        } catch (e) {

            console.error(e);

        }
    }


    useEffect(() => {
        fetchProductData();
    }, []);


    /*
    |--------------------------------------------------------------------------
    | EXPAND PRODUCT
    |--------------------------------------------------------------------------
    */

    function toggleExpandedProduct(
        product: Product
    ) {

        if (
            expandedProduct?.id ===
            product.id
        ) {

            setExpandedProduct(null);

            return;
        }

        setExpandedProduct(product);
    }

    return (
        <div>

            <Header />


            {productsData &&
                productsData.page &&
                productsData.page.headers && (

                    <PageHero
                        headers={
                            productsData.page.headers
                        }
                        page="products"
                    />

                )}


            {/*
            |--------------------------------------------------------------------------
            | DESKTOP CORE RANGE
            |--------------------------------------------------------------------------
            */}

            {productsData &&
                productsData.core &&
                productsData.core.products.length > 0 && (

                    <div className="range-outer core-range">

                        <div className="range-header">

                            <div className="text">
                                {
                                    productsData.core
                                        .name
                                } Range
                            </div>

                        </div>


                        <div
                            className="products-grid"
                            style={{
                                "--product-rows":
                                    Math.ceil(
                                        productsData
                                            .core
                                            .products
                                            .length / 5
                                    ),
                            } as React.CSSProperties}
                        >

                            {productsData.core.products.map(
                                (product) => {

                                    const selectedCap =
                                        selectedCaps[
                                            product.id
                                            ] ?? 0;

                                    const selectedSize =
                                        product.sizes[
                                            selectedCap
                                            ] ??
                                        product.sizes[0];

                                    return (
                                        <div
                                            key={
                                                product.id
                                            }
                                            onClick={() => navigate(`/product/${product.slug}`)}
                                            className={`product ${
                                                expandedProduct?.id ===
                                                product.id
                                                    ? "expanded"
                                                    : ""
                                            }`}
                                            style={{
                                                "--product-background-color":
                                                product.color_code,
                                            } as React.CSSProperties}
                                        >

                                            <img
                                                src={`${IMAGE_URL}/${selectedSize.pack_shot}`}
                                                alt={
                                                    product.name +
                                                    selectedSize.capacity
                                                }
                                            />

                                            <div className="product-info">

                                                <h2>
                                                    {
                                                        product.name
                                                    }
                                                </h2>

                                                <h3>
                                                    {
                                                        selectedSize.title
                                                    } (
                                                    {
                                                        selectedSize.capacity
                                                    }
                                                    )
                                                </h3>

                                                {expandedProduct &&
                                                    product.id ===
                                                    expandedProduct.id && (

                                                        <div>

                                                            <p>
                                                                {
                                                                    product.description
                                                                }
                                                            </p>

                                                            <div className="capacities">

                                                                {product.sizes.map(
                                                                    (
                                                                        size,
                                                                        index
                                                                    ) => (

                                                                        <div
                                                                            className={`capacities-item ${
                                                                                selectedCap ===
                                                                                index
                                                                                    ? "active"
                                                                                    : ""
                                                                            }`}
                                                                            key={
                                                                                size.id
                                                                            }
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();

                                                                                setSelectedCaps((prev) => ({
                                                                                    ...prev,
                                                                                    [product.id]: index,
                                                                                }));
                                                                            }}
                                                                        >
                                                                            {
                                                                                size.capacity
                                                                            }
                                                                        </div>

                                                                    )
                                                                )}

                                                            </div>

                                                        </div>

                                                    )}

                                                <div
                                                    className="bottom-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        toggleExpandedProduct(product);
                                                    }}
                                                >
                                                    {expandedProduct &&
                                                    product.id ===
                                                    expandedProduct.id
                                                        ? "-"
                                                        : "+"}
                                                </div>

                                            </div>

                                        </div>
                                    );
                                }
                            )}

                        </div>

                    </div>

                )}


            {/*
            |--------------------------------------------------------------------------
            | DESKTOP PREMIUM RANGE
            |--------------------------------------------------------------------------
            */}

            {productsData &&
                productsData.premium &&
                productsData.premium.products.length > 0 && (

                    <div className="range-outer">

                        <div className="range-header">

                            <div className="text">
                                {
                                    productsData
                                        .premium
                                        .name
                                } Range
                            </div>

                        </div>


                        <div
                            className="products-grid"
                            style={{
                                "--product-rows":
                                    Math.ceil(
                                        productsData
                                            .premium
                                            .products
                                            .length / 5
                                    ),
                            } as React.CSSProperties}
                        >

                            {productsData.premium.products.map(
                                (product) => {

                                    const selectedCap =
                                        selectedCaps[
                                            product.id
                                            ] ?? 0;

                                    const selectedSize =
                                        product.sizes[
                                            selectedCap
                                            ] ??
                                        product.sizes[0];

                                    return (
                                        <div
                                            key={
                                                product.id
                                            }
                                            onClick={() => navigate(`/product/${product.slug}`)}
                                            className={`product ${
                                                expandedProduct?.id ===
                                                product.id
                                                    ? "expanded"
                                                    : ""
                                            }`}
                                            style={{
                                                "--product-background-color":
                                                product.color_code,
                                            } as React.CSSProperties}
                                        >

                                            <img
                                                src={`${IMAGE_URL}/${selectedSize.pack_shot}`}
                                                alt={
                                                    product.name +
                                                    selectedSize.capacity
                                                }
                                            />

                                            <div className="product-info">

                                                <h2>
                                                    {
                                                        product.name
                                                    }
                                                </h2>

                                                <h3>
                                                    {
                                                        selectedSize.title
                                                    } (
                                                    {
                                                        selectedSize.capacity
                                                    }
                                                    )
                                                </h3>

                                                {expandedProduct &&
                                                    product.id ===
                                                    expandedProduct.id && (

                                                        <div>

                                                            <p>
                                                                {
                                                                    product.description
                                                                }
                                                            </p>

                                                            <div className="capacities">

                                                                {product.sizes.map(
                                                                    (
                                                                        size,
                                                                        index
                                                                    ) => (

                                                                        <div
                                                                            className={`capacities-item ${
                                                                                selectedCap ===
                                                                                index
                                                                                    ? "active"
                                                                                    : ""
                                                                            }`}
                                                                            key={
                                                                                size.id
                                                                            }
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();

                                                                                setSelectedCaps((prev) => ({
                                                                                    ...prev,
                                                                                    [product.id]: index,
                                                                                }));
                                                                            }}
                                                                        >
                                                                            {
                                                                                size.capacity
                                                                            }
                                                                        </div>

                                                                    )
                                                                )}

                                                            </div>

                                                        </div>

                                                    )}

                                                <div
                                                    className="bottom-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        toggleExpandedProduct(product);
                                                    }}
                                                >
                                                    {expandedProduct &&
                                                    product.id ===
                                                    expandedProduct.id
                                                        ? "-"
                                                        : "+"}
                                                </div>

                                            </div>

                                        </div>
                                    );
                                }
                            )}

                        </div>

                    </div>

                )}


            {/*
            |--------------------------------------------------------------------------
            | MOBILE CORE RANGE
            |--------------------------------------------------------------------------
            */}

            {productsData &&
                productsData.core &&
                productsData.core.products.length > 0 && (

                    <>
                        <div className="range-header mobile-range-header">
                            <div className="text">
                                {productsData.core.name} Range
                            </div>
                        </div>

                        <MobileProductRange
                            products={
                                productsData.core.products
                            }
                            selectedCaps={
                                selectedCaps
                            }
                            setSelectedCaps={
                                setSelectedCaps
                            }
                            expandedProduct={
                                expandedProduct
                            }
                            toggleExpandedProduct={
                                toggleExpandedProduct
                            }
                        />
                    </>

                )}


            {/*
            |--------------------------------------------------------------------------
            | MOBILE PREMIUM RANGE
            |--------------------------------------------------------------------------
            */}

            {productsData &&
                productsData.premium &&
                productsData.premium.products.length > 0 && (

                    <>
                        <div className="range-header mobile-range-header" >
                            <div className="text">
                                {productsData.premium.name} Range
                            </div>
                        </div>

                        <MobileProductRange
                            products={
                                productsData
                                    .premium
                                    .products
                            }
                            selectedCaps={
                                selectedCaps
                            }
                            setSelectedCaps={
                                setSelectedCaps
                            }
                            expandedProduct={
                                expandedProduct
                            }
                            toggleExpandedProduct={
                                toggleExpandedProduct
                            }
                        />
                    </>

                )}


            <FooterWithoutSignup />

        </div>
    );
}

export default Products;
